/**
 * The diagnostics panel. Small, collapsed by default, and the reason the project
 * exists.
 *
 * THREE VANTAGE POINTS ARE ON THIS SCREEN and the first two are not available to
 * a server-relayed architecture at all:
 *
 *   ① `getStats()` — RFC 3550 loss and jitter, plus the jitter-buffer and
 *     concealment counters, measured on the listener's own machine. Over a TCP
 *     relay these numbers CANNOT EXIST: loss shows up as latency and retransmits
 *     show up as jitter, indistinguishably. Here they are separate.
 *   ② the recorder's own counters — chunks staged, chunks the server has
 *     acknowledged, and the backlog between them. A non-zero backlog with a
 *     healthy call is the state this whole design is built to produce: the
 *     uplink is behind, and that is LATENCY, NOT LOSS.
 *   ③ THE DURABLE OBJECT'S OWN WORKING SET, read live from
 *     `GET /api/sessions/:id/state`. ① and ② are this browser describing itself.
 *     ③ is the only component that can see the provider sideband: whether our
 *     second socket into the session is attached THIS INSTANT, how many attaches
 *     have failed, how many recording bytes it is holding that S3 has not seen
 *     yet, whether the provider has ever sent an `output_audio_buffer.*` event on
 *     this session. None of that exists in a Postgres table, so there is nowhere
 *     else to read it from — and until this panel called it, nothing in the UI
 *     ever did.
 *
 * Every number in ① and ② is measured on THIS BROWSER'S CLOCK; every number in
 * ③ is measured on the Durable Object's. No provider realtime event carries a
 * wall-clock timestamp, so there is no authority to reconcile either against —
 * only the offset between the two, which is shown as its own field in both
 * blocks rather than folded silently into the others. The footnotes say so on
 * screen, because a latency figure with an unstated clock is a latency figure
 * somebody will eventually quote in a contract.
 */
import { useEffect, useState } from 'react'
import type { ReactNode } from 'react'
import { ChevronRight, RefreshCwIcon } from 'lucide-react'
import { cn } from '@/lib/utils'
import { ApiError, sessionState } from '@/api'
import { Button } from '@/components/ui/button'
import { Elapsed } from '@/components/call/call-status'
import {
  EvidencePill,
  RecordingPill,
  StatePill,
  fmtBytes,
  fmtClock,
  fmtInt,
  fmtMs,
} from '@/components/state-pill'
import type { CallSnapshot } from '@/lib/call'
import type { EvidenceState, RecordingState, SessionState } from '@voiceai/wire'

function Field({ label, children, hint }: { label: string; children: ReactNode; hint?: string }) {
  return (
    <div className="field" title={hint}>
      <span className="label">{label}</span>
      <span className="mono text-sm">{children}</span>
    </div>
  )
}

function n(value: number | undefined | null, unit = '', digits = 0): ReactNode {
  if (value == null || !Number.isFinite(value)) return <span className="faint">—</span>
  return `${digits > 0 ? value.toFixed(digits) : Math.round(value)}${unit}`
}

/**
 * The at-a-glance numbers, extracted from the collapsed panel so they can sit
 * BESIDE the live transcript rather than behind a click: connection states and
 * the per-window network figures. Everything here is this browser's clock —
 * the caveat that used to sit under these grids rides along at the bottom.
 */
export function LiveStats({ snap }: { snap: CallSnapshot }) {
  const s = snap.stats
  const c = snap.counters
  return (
    <div className="flex h-full flex-col gap-4">
      <div className="grid2">
        <Field label="peer connection">{snap.pc.connection ?? '—'}</Field>
        <Field label="ice">{snap.pc.ice ?? '—'}</Field>
        <Field label="control socket">
          {snap.controlUp ? 'up' : <span className="text-ev-warn">down — conversation unaffected</span>}
        </Field>
        <Field label="media uplink">
          {c.uplink === 'up' ? (
            'up'
          ) : c.uplink === 'off' ? (
            <span className="faint">off</span>
          ) : (
            <span className="text-ev-warn">{c.uplink}</span>
          )}
        </Field>
      </div>

      <div className="grid2">
        <Field label="round trip" hint="From the provider's own RTCP report where available.">
          {n(s?.roundTripTimeMs, ' ms', 1)}
        </Field>
        <Field label="jitter">{n(s?.jitterMs, ' ms', 1)}</Field>
        <Field
          label="jitter buffer"
          hint="jitterBufferDelay / jitterBufferEmittedCount over this window: how long the receiver held audio back to smooth this connection out. Latency caused by the network, not the model."
        >
          {n(s?.avgJitterBufferMs, ' ms', 1)}
        </Field>
        <Field label="packets in / lost" hint="Deltas over the last sampling window, not call totals.">
          {n(s?.packetsReceived)} / {n(s?.packetsLost)}
        </Field>
        <Field label="concealment" hint="Samples the receiver invented to cover loss. Non-zero means audible damage.">
          {n(s?.concealedSamples)} in {n(s?.concealmentEvents)} events
        </Field>
        <Field label="mic / speaker level">
          {n(s?.audioLevelOut, '', 3)} / {n(s?.audioLevelIn, '', 3)}
        </Field>
        <Field
          label="clock offset"
          hint="Minimum-RTT sample against the Durable Object. Not averaged: the least-delayed sample is the accurate one."
        >
          {n(snap.clock?.offsetMs, ' ms', 1)} <span className="faint">± rtt {n(snap.clock?.rttMs, ' ms', 1)}</span>
        </Field>
        <Field label="turns observed">{snap.turns.length}</Field>
      </div>

      <p className="mt-auto text-xs text-muted-foreground/80">
        Measured on this browser’s clock. The per-turn latency decomposition — VAD wait, admission,
        inference, network — is measured in the Durable Object and appears on the session page.
      </p>
    </div>
  )
}

export function DiagnosticsPanel({ snap }: { snap: CallSnapshot }) {
  const [open, setOpen] = useState(false)
  const c = snap.counters

  return (
    <section className="panel mb-0">
      <h2>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="flex items-center gap-1.5 uppercase tracking-wide"
          aria-expanded={open}
        >
          <ChevronRight className={cn('size-3 transition-transform', open && 'rotate-90')} aria-hidden />
          diagnostics
        </button>
        {/* PER TRACK, because the two halves are no longer captured the same way.
            This said "encoded transform · wire-exact" for the whole session, which
            was true of the user track and false of the assistant: Chromium never
            feeds a receiver-side transform, so the assistant is a Web Audio
            rendition of decoded playout. Claiming wire-exactness for a re-encode
            is exactly what §6 forbids. */}
        <span className="note">
          {snap.recorderPath === 'none'
            ? 'not recording'
            : snap.recorderPath === 'encoded-transform'
              ? 'user wire-exact · assistant rendition'
              : `rendition both tracks · ${snap.recorderPath}`}
        </span>
      </h2>
      {open ? (
        <div className="body flex flex-col gap-4">
          <div className="tablewrap">
            <table>
              <thead>
                <tr>
                  <th>track</th>
                  <th className="num">chunks</th>
                  <th className="num">acked</th>
                  <th className="num">backlog</th>
                  <th className="num">packets</th>
                  <th className="num">bytes</th>
                </tr>
              </thead>
              <tbody>
                {(['user', 'assistant'] as const).map((track) => (
                  <tr key={track}>
                    <td>{track}</td>
                    <td className="num mono">{c.chunks[track]}</td>
                    <td className="num mono">{c.acked[track] < 0 ? '—' : c.acked[track]}</td>
                    <td className={cn('num mono', c.backlog[track] > 4 && 'text-ev-warn')}>{c.backlog[track]}</td>
                    <td className="num mono">{c.packets[track].toLocaleString('en-GB')}</td>
                    <td className="num mono">{(c.bytes[track] / 1024).toFixed(1)} KiB</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="grid2">
            <Field label="socket buffered">{(c.bufferedAmount / 1024).toFixed(1)} KiB</Field>
            <Field label="opfs staging" hint="With staging, a media outage is latency. Without it, it is loss.">
              {c.staged ? 'durable' : <span className="text-ev-warn">memory only</span>}
            </Field>
            <Field label="upload lock">{c.locked ? 'held' : <span className="text-ev-warn">not held</span>}</Field>
            <Field label="reported gaps">
              {c.gaps === 0 ? '0' : <span className="font-semibold text-ev-bad">{c.gaps}</span>}
            </Field>
          </div>

          <p className="text-xs text-muted-foreground/80">
            Every figure above this line is measured on this browser’s clock. No provider realtime event carries a
            wall-clock timestamp, so the only reconciliation available is the offset against our own server, shown
            in the live numbers beside the transcript. The per-turn latency decomposition — VAD wait, admission,
            inference, network — is measured in the Durable Object and appears on the session page, not here.
          </p>

          <DoSnapshotBlock sessionId={snap.sessionId} live={snap.phase === 'live'} />
        </div>
      ) : null}
    </section>
  )
}

// ============================================================================
// ③ The Durable Object's working set — GET /api/sessions/:id/state
// ============================================================================

/**
 * HOW OFTEN WE READ THE OBJECT. Ten seconds, and it is a derived number rather
 * than a round one.
 *
 * The DO's own heartbeat is its alarm — `ALARM_FALLBACK_MS = 20_000` in
 * workers/session-do/src/env.ts, and the object reports its own period as
 * `alarmMs` in the payload below. The alarm is what re-attaches a dropped
 * sideband, flushes recording buffers and projects to Neon, so it is the cadence
 * at which most of these fields can change. Sampling at half the alarm period is
 * the longest interval that cannot skip a whole alarm cycle, so no state change
 * the alarm makes can hide between two consecutive reads.
 *
 * FASTER WOULD BE WORSE, and not marginally. This is the only read on any screen
 * that reaches the Durable Object instead of Postgres, and a request to a DO is
 * inbound activity: polling it keeps the object resident and billable. api.ts
 * puts it plainly — "on demand, or a slow refresh. Never a two-second timer."
 * The history screens poll Postgres at 5s and are welcome to; this one costs DO
 * wall time on a live call.
 *
 * THREE GATES SIT ON TOP OF THE INTERVAL, all of them free:
 *   - the parent panel is collapsed by default, so this component does not exist
 *     until a human has asked to look at it;
 *   - the timer only runs while the call is live, so a session nobody is on is
 *     never woken by a screen somebody left open;
 *   - a hidden tab skips its turn — a backgrounded demo has no reason to keep an
 *     object resident.
 * A manual read is always available, which is the right shape for a call that
 * has already ended: one deliberate wake, not a timer.
 */
const DO_POLL_MS = 10_000

/**
 * WHAT A FIELD OF THE SNAPSHOT TURNED OUT TO BE. Four cases, not two, and the
 * distinction between three of them is the whole reason this type exists.
 *
 * `GET /state` is explicitly NOT a contract — `Record<string, unknown>` in
 * api.ts, "whatever the DO finds worth reporting this week" — so there are three
 * different ways for a value to be absent and they make three different claims:
 *
 *   'null'    the object reported the key, and reported it as null. It looked and
 *             had nothing: `clockOffsetMs` before the first clock exchange,
 *             `expectedMsFromRtp` with no RTP anchors yet. A MEASURED absence.
 *   'absent'  the key is not in the payload at all. This build of the DO does not
 *             report it — version skew between the object and this panel. An
 *             UNMEASURED absence, which is not the same claim as the one above.
 *   'other'   the key is present with a type this panel did not expect. Shown
 *             raw, never coerced: a string where a number belongs is information,
 *             and `Number(x) || 0` would turn it into a confident zero.
 *
 * This is the PARTIAL-versus-UNVERIFIED distinction from docs/next-tasks.md §6
 * applied one level down, to the payload itself. Collapsing any of the three into
 * "0" or into a bare dash is exactly how a screen ends up saying "fine" about
 * something it never read.
 */
type Read<T> = { k: 'value'; v: T } | { k: 'null' } | { k: 'absent' } | { k: 'other'; raw: unknown }

function read<T>(body: Record<string, unknown>, key: string, is: (v: unknown) => v is T): Read<T> {
  if (!Object.hasOwn(body, key)) return { k: 'absent' }
  const raw = body[key]
  if (raw === null || raw === undefined) return { k: 'null' }
  if (is(raw)) return { k: 'value', v: raw }
  return { k: 'other', raw }
}

const isNum = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v)
const isBool = (v: unknown): v is boolean => typeof v === 'boolean'
const isStr = (v: unknown): v is string => typeof v === 'string'
const isArr = (v: unknown): v is unknown[] => Array.isArray(v)
const isObj = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v)

const num = (b: Record<string, unknown>, k: string) => read(b, k, isNum)
const bool = (b: Record<string, unknown>, k: string) => read(b, k, isBool)
const str = (b: Record<string, unknown>, k: string) => read(b, k, isStr)

/**
 * The three enums, validated before they reach a pill.
 *
 * `Record<SessionState, true>` rather than an array: the record is EXHAUSTIVE by
 * construction, so if @voiceai/wire adds a state this file stops compiling
 * instead of silently classifying the new value as unrecognised. The pills
 * themselves index a `Record<State, …>` and would render `undefined.label` on an
 * unknown string, so something has to check — and a snapshot whose shape is not a
 * contract is precisely where an unknown string can arrive.
 *
 * These would ideally live in state-pill.tsx next to the pills they guard. They
 * are here because this change owns this file; see the report.
 */
const SESSION_STATES: Record<SessionState, true> = {
  CREATED: true,
  LIVE: true,
  LIVE_DEGRADED_NO_SIDEBAND: true,
  LIVE_DEGRADED_NO_CONTROL: true,
  LIVE_DEGRADED_NO_MEDIA_UPLINK: true,
  ENDED_NORMAL: true,
  ENDED_TRANSFERRED: true,
  TAKEN_OVER: true,
  ENDED_TAKEN_OVER: true,
  ENDED_UNKNOWN: true,
  FAILED: true,
}
const EVIDENCE_STATES: Record<EvidenceState, true> = {
  PENDING: true,
  COMPLETE: true,
  PARTIAL: true,
  UNVERIFIED: true,
  ABSENT: true,
}
const RECORDING_STATES: Record<RecordingState, true> = {
  RECORDING: true,
  FINALIZING: true,
  READY: true,
  FAILED: true,
}

const isSessionState = (v: unknown): v is SessionState => isStr(v) && Object.hasOwn(SESSION_STATES, v)
const isEvidenceState = (v: unknown): v is EvidenceState => isStr(v) && Object.hasOwn(EVIDENCE_STATES, v)
const isRecordingState = (v: unknown): v is RecordingState => isStr(v) && Object.hasOwn(RECORDING_STATES, v)

/**
 * Every key this panel knows how to place. Two jobs, both load-bearing:
 * anything in the payload and NOT in this list is rendered raw underneath rather
 * than dropped, and anything in this list and NOT in the payload is named as
 * unread in the footnote. Between them, nothing the object says is discarded and
 * nothing it stays silent about is presented as a value.
 */
const SNAPSHOT_KEYS = [
  'sessionId',
  'atMs',
  'workerVersion',
  'alarmMs',
  'state',
  'evidence',
  'recordingState',
  'callId',
  'everAttached',
  'sidebandUp',
  'attaching',
  'attachFailures',
  'reattaches',
  'controlUp',
  'mediaUp',
  'sawAudioBufferEvents',
  'clockOffsetMs',
  'turns',
  'toolCalls',
  'bookings',
  'pendingBytes',
  'tracks',
] as const

/** Columns of one `tracks[]` entry — the DO's `TrackSummary`. */
const TRACK_KEYS = [
  'track',
  'trackId',
  'chunks',
  'packets',
  'bytes',
  'lastSeq',
  'actualMs',
  'expectedMsFromRtp',
  'pendingBytes',
] as const

/**
 * Reported, and reported as nothing. The object looked; there was no value.
 *
 * `label` because the honest phrase is not the same for every field: a null
 * `clockOffsetMs` is "we could not measure it", while a null `callId` is "there
 * is no provider call yet", and calling the second one unmeasured would be its
 * own small lie. The tooltip is common to both because the underlying fact is.
 */
function Unmeasured({ label = 'not measured' }: { label?: string }) {
  return (
    <span
      className="faint italic"
      title="The object reported this field and reported it as null: it looked, and there was no value to give. That is not a zero and it is not an error."
    >
      {label}
    </span>
  )
}

/**
 * Not in the payload at all — a different and worse thing than the above, so it
 * does not look like it. Dashed, like `ev-UNVERIFIED` in index.css, because a
 * dashed border survives a monochrome screenshot in a support ticket.
 */
function Unreported() {
  return (
    <span
      className="pill border border-dashed border-ev-warn-border text-ev-warn"
      title="This panel knows about this field and the object did not send it. Most likely the object is running a different build. Nothing here is claiming a value for it."
    >
      not reported
    </span>
  )
}

/** Present, but not the type this panel expected. Shown, never coerced. */
function Raw({ value }: { value: unknown }) {
  return (
    <span
      className="mono text-ev-warn"
      title="The object sent a type this panel did not expect for this field. It is printed verbatim rather than converted, because a conversion would invent a value."
    >
      {preview(value)}
    </span>
  )
}

function Val<T>({
  r,
  render,
  nullLabel,
}: {
  r: Read<T>
  render: (v: T) => ReactNode
  /** Override "not measured" where that is the wrong word for a null. */
  nullLabel?: string
}) {
  if (r.k === 'value') return <>{render(r.v)}</>
  if (r.k === 'null') return <Unmeasured label={nullLabel} />
  if (r.k === 'absent') return <Unreported />
  return <Raw value={r.raw} />
}

function preview(v: unknown): string {
  if (v === undefined) return 'undefined'
  let s: string
  try {
    s = JSON.stringify(v) ?? String(v)
  } catch {
    s = String(v)
  }
  return s.length > 160 ? `${s.slice(0, 160)}…` : s
}

/**
 * Whatever the object reported that this panel has no place for.
 *
 * The shape is not frozen, so the DO gaining a field must not mean the UI
 * quietly losing one. Anything unrecognised lands here as `key = json` rather
 * than being dropped on the floor — ugly on purpose, and the correct kind of
 * ugly: a reader can see there is something new and go and read the DO source.
 */
function Residual({
  body,
  known,
  label,
}: {
  body: Record<string, unknown>
  known: readonly string[]
  label: string
}) {
  const extra = Object.keys(body).filter((k) => !known.includes(k))
  if (extra.length === 0) return null
  return (
    <div className="field">
      <span className="label">{label}</span>
      <div className="flex flex-wrap gap-1.5">
        {extra.map((k) => (
          <span key={k} className="pill pill-plain">
            <span className="mono">{k}</span> = <span className="mono">{preview(body[k])}</span>
          </span>
        ))}
      </div>
    </div>
  )
}

function DoSnapshotBlock({ sessionId, live }: { sessionId: string | null; live: boolean }) {
  const [result, setResult] = useState<{ id: string; body: Record<string, unknown>; atMs: number } | null>(null)
  const [failed, setFailed] = useState<{ id: string; message: string } | null>(null)
  const [reading, setReading] = useState(false)
  const [tick, setTick] = useState(0)

  // One read per tick. Its own AbortController per run, because React 19 in
  // Strict Mode double-invokes this in development and the discarded run must
  // not land its response on top of the one that counts. No retry, ever: a
  // failed read shows as a failed read and the next tick tries again.
  useEffect(() => {
    if (sessionId === null) return
    const id = sessionId
    const ac = new AbortController()
    setReading(true)
    sessionState(id, ac.signal)
      .then((next) => {
        if (ac.signal.aborted) return
        setResult({ id, body: next, atMs: Date.now() })
        setFailed(null)
        setReading(false)
      })
      .catch((e: unknown) => {
        if (ac.signal.aborted) return
        // The last good snapshot is deliberately NOT cleared. A read that failed
        // does not unmake the one that worked — it makes it OLD, and an old
        // snapshot with its age on it is worth more than an empty panel. The
        // failure sits above it in a red box so the two cannot be confused.
        setFailed({
          id,
          message:
            e instanceof ApiError
              ? `${e.status}${e.code ? ` ${e.code}` : ''} — ${e.message}`
              : e instanceof Error
                ? e.message
                : String(e),
        })
        setReading(false)
      })
    return () => ac.abort()
  }, [sessionId, tick])

  useEffect(() => {
    if (!live || sessionId === null) return
    const id = setInterval(() => {
      // A tab nobody is looking at has no business waking a Durable Object.
      if (document.hidden) return
      setTick((t) => t + 1)
    }, DO_POLL_MS)
    return () => clearInterval(id)
  }, [live, sessionId])

  // A SNAPSHOT BELONGS TO THE SESSION IT WAS READ FOR, and is tagged with it
  // rather than cleared on change. Two calls in one tab is the normal demo — the
  // page offers "Start another call" — and without the tag the second call would
  // open showing the first call's object until the first read landed, which is a
  // screen stating numbers for a session it is not describing.
  const current = result !== null && result.id === sessionId ? result : null
  const body = current?.body ?? null
  const readAtMs = current?.atMs ?? null
  const failure = failed !== null && failed.id === sessionId ? failed.message : null

  // WHOSE SESSION DID THE OBJECT ANSWER FOR. Checked rather than assumed: a
  // Durable Object id is derived from the session id, so a mismatch would mean
  // something is very wrong upstream, and the empty string is a real value —
  // the DO's `sessionId` starts as '' and is only set when a request arrives
  // carrying the header, so it can genuinely answer before it knows who it is.
  const doId = body === null ? null : str(body, 'sessionId')
  const doIdValue = doId !== null && doId.k === 'value' ? doId.v : null
  const idMismatch = doIdValue !== null && doIdValue !== '' && doIdValue !== sessionId
  const idUnclaimed = doIdValue === ''
  const missing = body === null ? [] : SNAPSHOT_KEYS.filter((k) => !Object.hasOwn(body, k))
  const tracks = body === null ? null : read(body, 'tracks', isArr)

  return (
    <div className="rounded-md border">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b bg-muted/50 px-3 py-2">
        <span className="text-sm font-semibold">The Durable Object’s working set</span>
        <span className="text-xs text-muted-foreground">
          <span className="mono">GET /api/sessions/:id/state</span> · a debugging surface, read from the object
          itself rather than from Postgres
        </span>
        <span className="ml-auto flex items-center gap-2 text-xs text-muted-foreground">
          {readAtMs === null ? (
            <span className="faint">not read yet</span>
          ) : (
            <span title="How long ago this reading was taken. It is not refreshed between polls, so treat everything below as this old.">
              read <Elapsed fromMs={readAtMs} /> ago
              {live ? <> · every {Math.round(DO_POLL_MS / 1000)}s while live</> : ' · polling stopped'}
            </span>
          )}
          <Button
            variant="outline"
            size="xs"
            onClick={() => setTick((t) => t + 1)}
            disabled={sessionId === null || reading}
          >
            <RefreshCwIcon className={cn(reading && 'animate-spin')} aria-hidden />
            {reading ? 'reading' : 'read now'}
          </Button>
        </span>
      </div>

      <div className="flex flex-col gap-4 p-3">
        {sessionId === null ? (
          <p className="text-sm text-muted-foreground">
            No session has been created yet, so there is no object to read. This block fills in once a call starts.
          </p>
        ) : null}

        {failure !== null ? (
          <p className="err">
            Could not read the object: {failure}
            {body === null
              ? '. Nothing below is being claimed — this panel has no snapshot at all.'
              : '. The snapshot below is the last one that arrived, and its age is in the header. It is not current.'}
          </p>
        ) : null}

        {idMismatch ? (
          <p className="err">
            This snapshot names session <span className="mono">{doIdValue}</span>, not the one on this screen. Do
            not read the numbers below as this call’s.
          </p>
        ) : null}

        {idUnclaimed ? (
          <p className="text-sm text-ev-warn">
            The object has not learned its own session id yet, so it is answering before it has a row. The three
            states below are its defaults, not observations.
          </p>
        ) : null}

        {body === null ? null : (
          <>
            <div className="grid2">
              <Field
                label="session state"
                hint="The object's own state, from its SQLite row. The same enum the control channel pushes to the top of this screen: the channel pushes on change, this is a point-in-time read, so if the two disagree the one observed most recently is the current one."
              >
                <Val
                  r={read(body, 'state', isSessionState)}
                  render={(v) => <StatePill state={v} />}
                />
              </Field>
              <Field
                label="evidence"
                hint="Pre-finalisation, and not a claim about the audio. The object never awards COMPLETE or PARTIAL — both need the stitched artefact, which is the jobs worker's job. All it writes, and only when the call ends, is ABSENT (nothing was captured) or UNVERIFIED (a sideband re-attach or a known gap destroyed the measurement itself), and PENDING otherwise. A live call therefore reads PENDING."
              >
                <Val
                  r={read(body, 'evidence', isEvidenceState)}
                  render={(v) => <EvidencePill evidence={v} />}
                />
              </Field>
              <Field label="recording pipeline" hint="Where the artefact is, which is a different question from what can be claimed about it.">
                <Val
                  r={read(body, 'recordingState', isRecordingState)}
                  render={(v) => <RecordingPill state={v} />}
                />
              </Field>
              <Field
                label="worker version"
                hint="The git SHA of the build handling this call. A session-do deploy terminates every live WebSocket at once, and without this stamp that burst looks like a provider incident."
              >
                <Val r={str(body, 'workerVersion')} render={(v) => v} />
              </Field>
              <Field label="object clock" hint="The object's own Date.now() when it answered, not this browser's.">
                <Val r={num(body, 'atMs')} render={(v) => fmtClock(v)} />
              </Field>
              <Field
                label="alarm period"
                hint="The object's own heartbeat, as it reports it. Mandatory: an outbound WebSocket keeps an object alive for at most 15 minutes, after which 70-140s idle eviction resumes, and a phone call has no chunk stream to mask that. This panel reads every 10s, half the 20s default, so no alarm cycle passes unobserved between two reads — if the figure here is much larger than 20s, reads are further apart than one cycle."
              >
                <Val r={num(body, 'alarmMs')} render={(v) => fmtMs(v)} />
              </Field>
            </div>

            <div className="grid2">
              <Field
                label="sideband"
                hint="Our second WebSocket into the provider session. It carries the tools and the transcript and no audio, so losing it degrades the call — it does not end it. The conversation is browser-to-OpenAI and continues without us."
              >
                <Val
                  r={bool(body, 'sidebandUp')}
                  render={(v) =>
                    v ? 'attached' : <span className="font-semibold text-ev-warn">detached — no tools, no transcript</span>
                  }
                />
              </Field>
              <Field label="attaching" hint="An attach is in flight right now.">
                <Val r={bool(body, 'attaching')} render={(v) => (v ? 'in flight' : 'idle')} />
              </Field>
              <Field
                label="ever attached"
                hint="False after the SDP exchange means the sideband never came up at all: this call has had no tools and no transcript from the start."
              >
                <Val
                  r={bool(body, 'everAttached')}
                  render={(v) => (v ? 'yes' : <span className="text-ev-warn">never</span>)}
                />
              </Field>
              <Field label="consecutive attach failures" hint="Resets to zero on a successful attach. The DO gives up after a cap.">
                <Val
                  r={num(body, 'attachFailures')}
                  render={(v) => (v > 0 ? <span className="font-semibold text-ev-warn">{fmtInt(v)}</span> : '0')}
                />
              </Field>
              <Field label="re-attaches" hint="How many times the sideband has come back on this session. Each one is a window with no transcript and no tools.">
                <Val
                  r={num(body, 'reattaches')}
                  render={(v) => (v > 0 ? <span className="text-ev-warn">{fmtInt(v)}</span> : '0')}
                />
              </Field>
              <Field label="provider call id" hint="OpenAI's id for the CURRENT provider call. Null until the SDP exchange completes; a reconnect mints a new one. Never our key for anything.">
                <Val r={str(body, 'callId')} render={(v) => v} nullLabel="no provider call yet" />
              </Field>
              <Field label="control socket" hint="The browser's control channel, as the OBJECT sees it. Compare with the browser's own view higher up this panel.">
                <Val
                  r={bool(body, 'controlUp')}
                  render={(v) => (v ? 'up' : <span className="text-ev-warn">down</span>)}
                />
              </Field>
              <Field label="media socket" hint="The binary recording uplink, as the object sees it. Down means chunks are accumulating in the browser.">
                <Val
                  r={bool(body, 'mediaUp')}
                  render={(v) => (v ? 'up' : <span className="text-ev-warn">down</span>)}
                />
              </Field>
              <Field
                label="output_audio_buffer.* seen"
                hint="Whether the provider has ever sent output_audio_buffer.started/.stopped/.cleared on this session. That event is the provider's own mark for when it began pushing RTP, and it is the anchor every inference figure is measured from. Expected to be absent before the first assistant turn; after one, its absence means this call has no turn timing at all."
              >
                <Val
                  r={bool(body, 'sawAudioBufferEvents')}
                  render={(v) => (v ? 'yes' : <span className="text-ev-warn">none yet</span>)}
                />
              </Field>
              <Field
                label="clock offset"
                hint="The object's estimate of this browser's clock against its own, from the minimum-RTT exchange on the control channel. Null until the first exchange lands — which means the network figure on the session page has no reconciliation yet."
              >
                <Val r={num(body, 'clockOffsetMs')} render={(v) => fmtMs(v)} />
              </Field>
            </div>

            <div className="grid2">
              <Field label="turns" hint="Rows in the object's own turns table. Its count, not the browser's.">
                <Val r={num(body, 'turns')} render={(v) => fmtInt(v)} />
              </Field>
              <Field label="tool calls" hint="Executed in the object over the sideband. The browser is not a participant and cannot forge or suppress one.">
                <Val r={num(body, 'toolCalls')} render={(v) => fmtInt(v)} />
              </Field>
              <Field
                label="buffered, not yet in S3"
                hint="Audio the object is holding that S3 has not seen. It flushes at 64 KiB or 30s, whichever comes first. Until it does, this audio exists in exactly one place."
              >
                <Val r={num(body, 'pendingBytes')} render={(v) => fmtBytes(v)} />
              </Field>
            </div>

            {tracks === null ? null : tracks.k !== 'value' ? (
              <Field label="tracks">
                <Val r={tracks} render={() => null} />
              </Field>
            ) : tracks.v.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                The object reports no tracks yet. That is not zero bytes recorded — it is no track row to report,
                which is the normal state until the first media frame arrives.
              </p>
            ) : (
              <div className="flex flex-col gap-2">
                <div className="tablewrap">
                  <table>
                    <thead>
                      <tr>
                        <th>track</th>
                        <th className="num">chunks</th>
                        <th className="num">packets</th>
                        <th className="num">bytes</th>
                        <th className="num">last seq</th>
                        <th className="num">held</th>
                        <th className="num">rtp span</th>
                        <th className="num">unflushed</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tracks.v.map((entry, i) => {
                        if (!isObj(entry)) {
                          return (
                            <tr key={i}>
                              <td colSpan={8}>
                                <Raw value={entry} />
                              </td>
                            </tr>
                          )
                        }
                        const name = str(entry, 'track')
                        return (
                          <tr key={name.k === 'value' ? name.v : i}>
                            <td>
                              <Val r={name} render={(v) => v} />
                            </td>
                            <td className="num mono">
                              <Val r={num(entry, 'chunks')} render={(v) => fmtInt(v)} />
                            </td>
                            <td className="num mono">
                              <Val r={num(entry, 'packets')} render={(v) => fmtInt(v)} />
                            </td>
                            <td className="num mono">
                              <Val r={num(entry, 'bytes')} render={(v) => fmtBytes(v)} />
                            </td>
                            <td className="num mono">
                              <Val r={num(entry, 'lastSeq')} render={(v) => fmtInt(v)} nullLabel="no chunks yet" />
                            </td>
                            <td className="num mono" title="What the object actually holds: packets x 20 ms.">
                              <Val r={num(entry, 'actualMs')} render={(v) => fmtMs(v)} />
                            </td>
                            <td
                              className="num mono"
                              title="Oracle 2, and the sideband-independent one: first RTP timestamp to the end of the last packet, gaps included. Null when there are no anchors to derive it from — which is 'we could not measure', not 'nothing is missing'."
                            >
                              <Val r={num(entry, 'expectedMsFromRtp')} render={(v) => fmtMs(v)} />
                            </td>
                            <td className="num mono">
                              <Val r={num(entry, 'pendingBytes')} render={(v) => fmtBytes(v)} />
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
                {tracks.v.map((entry, i) => {
                  if (!isObj(entry)) return null
                  const name = str(entry, 'track')
                  return (
                    <Residual
                      key={i}
                      body={entry}
                      known={TRACK_KEYS}
                      label={`also on track ${name.k === 'value' ? name.v : i}`}
                    />
                  )
                })}
              </div>
            )}

            <Residual body={body} known={SNAPSHOT_KEYS} label="also reported, and not placed above" />

            <SnapshotLimits missing={missing} live={live} />
          </>
        )}
      </div>
    </div>
  )
}

/**
 * WHAT THIS BLOCK CANNOT TELL YOU. A product feature, not a disclaimer — the same
 * rule as section 6 of the session page, and for the same reason: the half of the
 * claim that admits a limit is the half that makes the other half believable.
 *
 * Two of these are computed from THIS snapshot rather than being boilerplate,
 * which is the difference between a caveat and a diagnosis.
 */
function SnapshotLimits({ missing, live }: { missing: readonly string[]; live: boolean }) {
  return (
    <ul className="ml-4 flex max-w-4xl list-disc flex-col gap-1.5 text-xs text-muted-foreground">
      {missing.length > 0 && (
        <li>
          <strong className="font-semibold text-foreground">
            {missing.length} field{missing.length === 1 ? '' : 's'} this panel knows about {missing.length === 1 ? 'was' : 'were'} not
            in the payload
          </strong>{' '}
          — <span className="mono">{missing.join(', ')}</span>. The shape of this endpoint is deliberately not a
          contract, so the likely cause is that the object is running a different build. Those fields are shown as
          unreported above; nothing here is guessing a value for them.
        </li>
      )}
      <li>
        <strong className="font-semibold text-foreground">These three states are the object’s own defaults when it has no row.</strong>{' '}
        Session state falls back to <span className="mono">CREATED</span>, evidence to{' '}
        <span className="mono">PENDING</span> and the pipeline to <span className="mono">RECORDING</span> when its
        SQLite row is missing, so those three values are not proof that a row exists.
      </li>
      <li>
        <strong className="font-semibold text-foreground">This screen cannot show you a COMPLETE recording.</strong>{' '}
        The object never awards <span className="mono">COMPLETE</span> or <span className="mono">PARTIAL</span>:
        both need the stitched artefact and both belong to the jobs worker. At the end of a call the object writes{' '}
        <span className="mono">ABSENT</span> when nothing was captured, or <span className="mono">UNVERIFIED</span>{' '}
        when a sideband re-attach or a known gap destroyed the measurement itself — a failure to measure, which is
        a different claim from <span className="mono">PARTIAL</span>&rsquo;s measured gap and is never shown as
        one. Everything else stays <span className="mono">PENDING</span>, which is not a defect.
      </li>
      <li>
        <strong className="font-semibold text-foreground">The track figures are not a completeness measurement.</strong>{' '}
        &ldquo;Held&rdquo; is what the object has (packets × 20 ms); &ldquo;rtp span&rdquo; is the RTP timeline it
        covers, gaps included. Mid-call the difference is mostly audio still in flight from the browser, not audio
        that is missing, and the barge-in correction has not been calibrated yet — so no verdict is drawn from the
        two here, deliberately.
      </li>
      <li>
        <strong className="font-semibold text-foreground">Every figure in this block is on the object’s clock</strong>{' '}
        and was true at the moment it answered, not now.{' '}
        {live ? (
          <>
            Between reads it is up to {Math.round(DO_POLL_MS / 1000)}s out of date, by design: a request to a
            Durable Object is inbound activity, so reading it faster keeps the object resident and costs wall time.
          </>
        ) : (
          <>
            Reads stop when the call is not live, so this is a still frame from when it was. Use &ldquo;read
            now&rdquo; for a fresh one — that wakes the object deliberately, which is the only sensible way to touch
            a session that has ended.
          </>
        )}
      </li>
      <li>
        <strong className="font-semibold text-foreground">A number this endpoint does not carry is not a zero.</strong>{' '}
        &ldquo;Not measured&rdquo; above means the object looked and had no value; &ldquo;not reported&rdquo; means
        it never sent the field. Neither is the same as a measurement of zero, and this panel does not render one
        as the other.
      </li>
    </ul>
  )
}
